import type {
  AnswerContextCompilation,
  AnswerContextFact,
} from './answer-context-compilation';
import type { ExecutiveAnswerRecommendationPolicy } from './executive-answer-contract';

/**
 * #4619 Slice 5 — the MINIMAL outbound answer validator.
 *
 * Scope is pinned (Codex 5311107373; APPROVE 5312781598 gate paragraph):
 * EXACTLY four checks, all evaluated from the compilation artifact and the
 * final narration section templates — diagnostic/score/log ONLY. This module
 * is NOT a release gate, render suppressor, UI/SSE adjudicator, state
 * machine, or second answer authority; nothing here may block, rewrite, or
 * demote an answer. It contains no industry language and no metric-key
 * special cases — every judgment traces to the compiled contract artifact.
 *
 * CONTRACT-INTEGRITY REPAIRS (Codex 5317276544, both blocking findings):
 *
 *   - Check 1 iterates `compilation.requiredObligations` — the authoritative
 *     inventory the compiler projects straight off the resolved contract —
 *     never the compiler's own admission output. A compiler defect that
 *     drops a required obligation from `requiredFacts` AND `namedGaps` is
 *     therefore a FINDING here, not a silence (finding 1: the old walk over
 *     `requiredFacts[].admissions` could only prove "admitted but unrendered",
 *     never "required but never admitted").
 *
 *   - Checks 1–3 judge EXACT bindingRefs through the caller-supplied
 *     `renderedBindingReceipt` — minted PER SECTION at the renderer's own
 *     binding-selection seam (Codex 5317586782: the renderer resolves each
 *     section's bindings via `numericBindingsForSection`, which can swap in
 *     a value-equivalent same-placeholder binding when the section cites
 *     that binding's evidence id — so the same placeholder can legitimately
 *     resolve to DIFFERENT exact bindings in different sections). Each
 *     receipt entry carries (section, placeholder, bindingRef) from the
 *     Slice-4 central-resolver provenance; a rendered `{{n:placeholder}}`
 *     token is judged against ITS OWN section's entry, so two governed
 *     bindings sharing a placeholder while differing in scope, period,
 *     evidence, or value are judged by the binding that actually rendered
 *     in that section, never by the shared label and never by a global
 *     first-match (finding 2 + its 5317586782 follow-up). This module never
 *     re-walks a pool, never `.find()`s over bindings, and never parses
 *     prose for identity: the receipt IS the canonical mapping.
 *
 *   1. required_obligation_unanswered — a required obligation of the
 *      contract inventory with no rendered admitted fact AND no honest
 *      qualification (compiler named gap or seam construction diagnostic
 *      naming the obligation).
 *   2. prohibited_substitution_rendered — a substitution the compiler's
 *      closed role table already flagged, whose EXACT binding actually
 *      reached the prose (the compiler detects; the validator reports only
 *      when the forbidden binding itself is on the page).
 *   3. claim_outside_compiled_context — a rendered numeric token that no
 *      receipt entry serves, or whose exact rendered binding no compiled
 *      fact admits.
 *   4. recommendation_policy_violation — recommendation content where the
 *      contract says not_applicable, or none where it says required.
 */

export const ANSWER_CONTEXT_OUTBOUND_CHECKS = Object.freeze([
  'required_obligation_unanswered',
  'prohibited_substitution_rendered',
  'claim_outside_compiled_context',
  'recommendation_policy_violation',
] as const);

export type AnswerContextOutboundCheck =
  (typeof ANSWER_CONTEXT_OUTBOUND_CHECKS)[number];

export type AnswerContextOutboundFinding = {
  readonly check: AnswerContextOutboundCheck;
  /** The obligation the finding concerns, when one is identifiable. */
  readonly obligationId: string | null;
  /** The placeholder the finding concerns, when one is identifiable. */
  readonly placeholder: string | null;
  /** One reader-neutral sentence naming the defect. No industry nouns. */
  readonly detail: string;
};

export type AnswerContextOutboundValidation = {
  readonly findings: readonly AnswerContextOutboundFinding[];
  /** Findings per check, every check present even at zero (counter-ready). */
  readonly countsByCheck: Readonly<Record<AnswerContextOutboundCheck, number>>;
};

/** The {{n:placeholder}} tokens of one template, without the wrapper. */
function numericTokenPlaceholders(templateMarkdown: string): string[] {
  const out: string[] = [];
  const pattern = /\{\{n:([a-zA-Z0-9_.[\]-]+)\}\}/g;
  let hit: RegExpExecArray | null;
  while ((hit = pattern.exec(templateMarkdown)) !== null) out.push(hit[1]);
  return out;
}

/** The four narration sections the outbound validator judges. Closed set. */
export const ANSWER_CONTEXT_NARRATION_SECTIONS = Object.freeze([
  'headline',
  'whatChanged',
  'recommendation',
  'rationale',
] as const);

export type AnswerContextNarrationSection =
  (typeof ANSWER_CONTEXT_NARRATION_SECTIONS)[number];

/**
 * One entry of the render receipt (Codex 5317276544 finding 2, repaired per
 * 5317586782): the EXACT binding the renderer's own section
 * binding-selection step chose for `placeholder` in `section`, identified by
 * its Slice-4 `bindingRef`. Minted AFTER `numericBindingsForSection` has
 * selected — never reconstructed from a global pool order, because the
 * selection is evidence-aware per section and the same placeholder can
 * resolve to different exact bindings in different sections.
 */
export type AnswerContextRenderedBindingReceiptEntry = {
  readonly section: AnswerContextNarrationSection;
  readonly bindingRef: string;
  readonly placeholder: string;
  /** Contract-authorized metric/referent identities served by this binding. */
  readonly metricReferents: readonly string[];
};

/** Channels whose `admittedBy` is a contract obligationId (never a clause ref). */
const OBLIGATION_ADMISSION_CHANNELS: ReadonlySet<string> = new Set([
  'required_subject_metric',
  'optional_subject_metric',
  'decision_obligation',
]);

/**
 * Pure, total, diagnostic-only. `sections` are the FINAL narration section
 * templates (post-validation/render); absent sections read as empty.
 * `renderedBindingReceipt` is the seam's Slice-4 render-resolution
 * provenance (see the type doc) — the ONLY source of rendered-binding
 * identity here. `constructionDiagnostics` is the seam's loss record — a
 * lost required fact whose obligation the seam named counts as honestly
 * qualified, not unanswered (one-path law: declared absence is a passing
 * state).
 */
export function validateAnswerContextOutbound(params: {
  compilation: AnswerContextCompilation;
  recommendationPolicy: ExecutiveAnswerRecommendationPolicy;
  sections: Partial<
    Record<'headline' | 'whatChanged' | 'recommendation' | 'rationale', string>
  >;
  renderedBindingReceipt: readonly AnswerContextRenderedBindingReceiptEntry[];
  constructionDiagnostics?: ReadonlyArray<{
    readonly reason: string;
    readonly placeholder: string | null;
    readonly obligationIds: readonly string[];
  }>;
}): AnswerContextOutboundValidation {
  const findings: AnswerContextOutboundFinding[] = [];
  const diagnostics = params.constructionDiagnostics ?? [];

  // The exact-binding identity of everything on the page, PER SECTION
  // (Codex 5317586782): each rendered token maps to its own section's
  // receipt entry — the binding the renderer's section selection actually
  // chose — never to a global first-match across sections.
  const receiptBySectionToken = new Map<
    string,
    { bindingRef: string; metricReferents: ReadonlySet<string> }
  >();
  for (const entry of params.renderedBindingReceipt ?? []) {
    const key = `${entry.section}\u0000${entry.placeholder}`;
    if (!receiptBySectionToken.has(key)) {
      receiptBySectionToken.set(key, {
        bindingRef: entry.bindingRef,
        metricReferents: new Set(entry.metricReferents),
      });
    }
  }
  /** Every rendered token occurrence with its exact per-section identity. */
  const renderedTokens: Array<{
    readonly section: AnswerContextNarrationSection;
    readonly placeholder: string;
    readonly bindingRef: string | null;
    readonly metricReferents: ReadonlySet<string>;
  }> = [];
  for (const section of ANSWER_CONTEXT_NARRATION_SECTIONS) {
    const tokens = new Set(
      numericTokenPlaceholders(params.sections[section] ?? ''),
    );
    for (const placeholder of tokens) {
      const receipt = receiptBySectionToken.get(
        `${section}\u0000${placeholder}`,
      );
      renderedTokens.push({
        section,
        placeholder,
        bindingRef: receipt?.bindingRef ?? null,
        metricReferents: receipt?.metricReferents ?? new Set(),
      });
    }
  }
  const renderedBindingRefs = new Set<string>();
  for (const token of renderedTokens) {
    if (token.bindingRef) renderedBindingRefs.add(token.bindingRef);
  }

  const allFacts: AnswerContextFact[] = [
    ...params.compilation.requiredFacts,
    ...params.compilation.supportingFacts,
    ...params.compilation.permittedDecisionFacts,
  ];

  // 1. Every required obligation of the AUTHORITATIVE contract inventory is
  // answered by an exactly-rendered admitted fact, or honestly qualified.
  const gapObligations = new Set(
    params.compilation.namedGaps.map((gap) => gap.obligationId),
  );
  const diagnosedObligations = new Set(
    diagnostics.flatMap((entry) => entry.obligationIds),
  );
  const factsByObligation = new Map<string, AnswerContextFact[]>();
  for (const fact of allFacts) {
    for (const admission of fact.admissions) {
      if (!OBLIGATION_ADMISSION_CHANNELS.has(admission.channel)) continue;
      if (!admission.admittedBy) continue;
      const list = factsByObligation.get(admission.admittedBy) ?? [];
      if (!list.includes(fact)) list.push(fact);
      factsByObligation.set(admission.admittedBy, list);
    }
  }
  for (const obligation of params.compilation.requiredObligations ?? []) {
    const obligationId = obligation.obligationId;
    const facts = factsByObligation.get(obligationId) ?? [];
    const answered = facts.some((fact) =>
      renderedTokens.some(
        (token) =>
          token.bindingRef === fact.bindingRef &&
          fact.admissions.some(
            (admission) =>
              admission.admittedBy === obligationId &&
              token.metricReferents.has(admission.metricKey),
          ),
      ),
    );
    if (answered) continue;
    // DECLARED ABSENCE IS A PASSING STATE (#4619 ceremony amendment, Codex
    // review; standing correction at #4602 comment 5331084951). A
    // `gapOnlyRequiredSet` special case used to sit here and emit
    // required_obligation_unanswered for EVERY obligation whenever all
    // required obligations were gap-covered and requiredFacts was empty —
    // reached BEFORE the gap-covered continue below, so a wholly honest
    // answer scored worst. That inverted this module's own check-1 contract
    // (see the header: a finding requires no rendered admitted fact AND no
    // honest qualification, where a compiler named gap IS that
    // qualification). The normal one-path rule governs again: a named gap
    // continues, exactly as it does when only some obligations are gapped.
    if (gapObligations.has(obligationId)) continue;
    if (diagnosedObligations.has(obligationId)) continue;
    findings.push({
      check: 'required_obligation_unanswered',
      obligationId,
      placeholder: facts[0]?.placeholder ?? null,
      detail:
        facts.length === 0
          ? 'A required obligation of the resolved contract has no admitted ' +
            'governed fact, no named gap, and no construction diagnostic — ' +
            'the compiled artifact is silent about it.'
          : 'A required obligation has an admitted governed figure, but the ' +
            'answer neither renders it nor declares its absence.',
    });
  }

  // 2. Prohibited substitutions whose EXACT binding reached the prose. The
  // compiler records the offender's bindingRef; the judgment is that ref
  // against the receipt-resolved page — never the shared placeholder label.
  for (const substitution of params.compilation.prohibitedSubstitutions) {
    if (!renderedBindingRefs.has(substitution.bindingRef)) continue;
    findings.push({
      check: 'prohibited_substitution_rendered',
      obligationId: substitution.obligationId,
      placeholder: substitution.placeholder,
      detail:
        `A figure whose declared role is '${substitution.offeredRole}' is ` +
        `rendered against an obligation requiring '${substitution.claimedRole}'.`,
    });
  }

  // 3. No material claim outside the compiled context: every rendered token
  // must resolve, IN ITS OWN SECTION, through the receipt to a binding some
  // compiled fact admits. A token its section's receipt does not serve, or
  // whose exact rendered binding is not an admitted fact's binding, is
  // outside the context — proven by ref, per section, never assumed from a
  // matching label elsewhere on the page.
  for (const token of renderedTokens) {
    const admitted = allFacts.some(
      (fact) =>
        fact.bindingRef === token.bindingRef &&
        (fact.admissions.length === 0 ||
          fact.admissions.some((admission) =>
            token.metricReferents.has(admission.metricKey),
          )),
    );
    if (admitted) continue;
    findings.push({
      check: 'claim_outside_compiled_context',
      obligationId: null,
      placeholder: token.placeholder,
      detail:
        `A numeric claim in the '${token.section}' section is rendered that ` +
        'no clause of the compiled answer context admits.',
    });
  }

  // 4. Recommendation-policy conformance.
  const recommendationText = (params.sections.recommendation ?? '').trim();
  if (params.recommendationPolicy === 'not_applicable' && recommendationText) {
    findings.push({
      check: 'recommendation_policy_violation',
      obligationId: null,
      placeholder: null,
      detail:
        'The contract declares recommendations not applicable to this ' +
        'answer shape, but the recommendation section carries content.',
    });
  }
  if (params.recommendationPolicy === 'required' && !recommendationText) {
    findings.push({
      check: 'recommendation_policy_violation',
      obligationId: null,
      placeholder: null,
      detail:
        'The contract requires a recommendation, but the recommendation ' +
        'section is empty.',
    });
  }

  const countsByCheck = Object.fromEntries(
    ANSWER_CONTEXT_OUTBOUND_CHECKS.map((check) => [
      check,
      findings.filter((finding) => finding.check === check).length,
    ]),
  ) as Record<AnswerContextOutboundCheck, number>;
  return { findings, countsByCheck };
}
